import type { ReportTable, ReportRow } from './print.js';
import { company, companyLocation, printIdentifiers } from './reference-data.js';

/**
 * Writing an `.xlsx`, in the browser, with no dependencies.
 *
 * The mirror of `xlsx.ts`, which reads one for the voucher bulk import, and
 * built the same way and for the same reason: an xlsx is a ZIP of XML, and the
 * alternative is SheetJS — pinned at 0.18.5 on npm with an unpatched
 * prototype-pollution advisory, with the patched builds published only to the
 * vendor's own CDN. That is a supply chain this repository does not need in
 * order to write a grid of cells.
 *
 * **The ZIP members are stored, not deflated.** `CompressionStream` is async and
 * a ZIP wants each member's CRC and length in a header *before* its data, so
 * deflating would make this an async pipeline for the sake of a file that is
 * some tens of kilobytes of text. Stored entries are valid ZIP and Excel opens
 * them without comment.
 *
 * **Strings are inline.** A shared-strings part is a size optimisation for a
 * workbook that repeats values thousands of times; `inlineStr` needs no second
 * part and no index to keep in step with the sheet.
 *
 * ── What makes an export functional rather than decorative
 *
 * A column that is a number in the report is written as a **number**, so the
 * first thing anybody does with an exported trial balance — select the column
 * and read the sum — works. `1,234.56` is unpicked back into `1234.56`; an
 * account code like `1-1-2-3`, a date, and a balance carrying its `Dr`/`Cr`
 * side stay text, because each of those is a label that merely looks numeric and
 * coercing it would be the export quietly changing the report.
 */

// ── CRC-32 ───────────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of bytes) c = CRC_TABLE[(c ^ byte) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

// ── ZIP ──────────────────────────────────────────────────────────────────────

interface Member {
  name: string;
  data: Uint8Array;
}

/**
 * A ZIP of stored members.
 *
 * Local header, data, local header, data … then the central directory and the
 * end-of-central-directory record. Bit 11 of the general-purpose flags marks the
 * names as UTF-8, which matters only for a file whose sheet is not ASCII — but
 * costs nothing and is the one thing a reader cannot infer.
 */
function zip(members: Member[]): Blob {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const member of members) {
    const name = encoder.encode(member.name);
    const crc = crc32(member.data);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // UTF-8 names
    lv.setUint16(8, 0, true); // stored
    lv.setUint16(10, 0, true); // time — a fixed 1980-01-01, so the same
    lv.setUint16(12, 33, true); // report exports byte-for-byte identically
    lv.setUint32(14, crc, true);
    lv.setUint32(18, member.data.length, true);
    lv.setUint32(22, member.data.length, true);
    lv.setUint16(26, name.length, true);
    local.set(name, 30);

    const entry = new Uint8Array(46 + name.length);
    const cv = new DataView(entry.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 33, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, member.data.length, true);
    cv.setUint32(24, member.data.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    entry.set(name, 46);

    chunks.push(local, member.data);
    central.push(entry);
    offset += local.length + member.data.length;
  }

  const directorySize = central.reduce((sum, e) => sum + e.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, members.length, true);
  ev.setUint16(10, members.length, true);
  ev.setUint32(12, directorySize, true);
  ev.setUint32(16, offset, true);

  return new Blob([...chunks, ...central, end] as BlobPart[], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ── Spreadsheet ──────────────────────────────────────────────────────────────

function xmlEscape(value: string): string {
  return (
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      // XML 1.0 forbids most control characters outright, and a stray one makes
      // Excel declare the whole workbook unreadable rather than skipping the cell.
      // Tab, newline and carriage return are the three it does allow.
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
  );
}

/**
 * `1,234.56` → `1234.56`, and everything else → null.
 *
 * Deliberately strict. Optional leading minus or a parenthesised negative,
 * digits with optional thousands commas, an optional decimal part, nothing else
 * — so `2026-08-18`, `48,500.00 Cr` and `12 BAG` all stay text.
 *
 * **And it is only asked about a right-aligned column.** That is the signal the
 * report already carries: money and quantities are right-aligned, labels are
 * not. Judging cell by cell instead made a column disagree with itself — the top
 * of a chart of accounts is the code `1`, which is a perfectly good number,
 * while `1-1-2-3` four rows below it is not, so one column arrived in Excel as
 * a mix of numbers and text sorting in two different orders.
 */
function asNumber(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const negated = /^\((.+)\)$/.exec(trimmed);
  const body = negated ? negated[1]!.trim() : trimmed;
  if (!/^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(body) && !/^-?\d+(\.\d+)?$/.test(body)) return null;
  const plain = body.replace(/,/g, '');
  if (!Number.isFinite(Number(plain))) return null;
  return negated ? `-${plain}` : plain;
}

/** `0` → `A`, `26` → `AA`. Base-26 with no zero digit. */
function columnName(index: number): string {
  let name = '';
  let n = index;
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
}

/** Excel's own limits: 31 characters, and five punctuation marks it reserves. */
function sheetName(title: string): string {
  const cleaned = title.replace(/[[\]:*?/\\]/g, ' ').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
}

function sheetXml(rows: string[][], bold: Set<number>, numericColumns: Set<number>): string {
  const cells = rows
    .map((row, r) => {
      const style = bold.has(r) ? ' s="1"' : '';
      const body = row
        .map((value, c) => {
          const reference = `${columnName(c)}${r + 1}`;
          if (value === '') return '';
          const numeric = numericColumns.has(c) ? asNumber(value) : null;
          return numeric === null
            ? `<c r="${reference}"${style} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`
            : `<c r="${reference}"${style}><v>${numeric}</v></c>`;
        })
        .join('');
      return `<row r="${r + 1}">${body}</row>`;
    })
    .join('');

  // Column widths from the longest cell, so nothing opens as `#######`. Capped,
  // because one long narration must not push the money columns off the screen.
  const widths = rows
    .reduce<number[]>((widest, row) => {
      row.forEach((value, c) => {
        widest[c] = Math.max(widest[c] ?? 8, Math.min(48, value.length + 2));
      });
      return widest;
    }, [])
    .map((width, i) => `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<cols>${widths}</cols><sheetData>${cells}</sheetData></worksheet>`;
}

/** Two named styles: plain, and bold for the heading and total rows. */
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="1"><fill><patternFill patternType="none"/></fill></fills>
<borders count="1"><border/></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
</styleSheet>`;

/** A workbook of one sheet, as bytes. */
function workbook(
  name: string,
  rows: string[][],
  bold: Set<number>,
  numericColumns: Set<number>,
): Blob {
  const encoder = new TextEncoder();
  const part = (path: string, xml: string): Member => ({ name: path, data: encoder.encode(xml) });

  return zip([
    part(
      '[Content_Types].xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
    ),
    part(
      '_rels/.rels',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    ),
    part(
      'xl/workbook.xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
 xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${xmlEscape(name)}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    ),
    part(
      'xl/_rels/workbook.xml.rels',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    ),
    part('xl/styles.xml', STYLES_XML),
    part('xl/worksheets/sheet1.xml', sheetXml(rows, bold, numericColumns)),
  ]);
}

// ── The export ───────────────────────────────────────────────────────────────

function download(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn rather than immediately: Safari reads the blob
  // asynchronously after the click and a synchronous revoke saves an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'report'
  );
}

function reportRow(row: ReportRow): { cells: string[]; strong: boolean } {
  return Array.isArray(row)
    ? { cells: row, strong: false }
    : { cells: row.cells, strong: Boolean(row.head || row.total) };
}

/**
 * The same report, as a spreadsheet.
 *
 * It takes exactly what `printReport` takes, and that is the point: a screen
 * declares its table once and gets both, so the printed copy and the exported
 * one cannot drift into disagreeing about a column. An export that showed a
 * column the print lacked would be two reports with one name.
 *
 * The heading block above the table is the letterhead in the four rows the print
 * draws — company, legal name, address, contact — then the identifiers the
 * profile says to print, then the report's own name and the window it covers.
 * A spreadsheet that opens with a bare grid of numbers is one nobody can file:
 * three months later it is impossible to say which company it is, or as at when.
 */
export function exportReport(report: ReportTable): void {
  const co = company();
  const ids = printIdentifiers();

  const rows: string[][] = [];
  const bold = new Set<number>();
  const push = (cells: string[], strong = false) => {
    if (strong) bold.add(rows.length);
    rows.push(cells);
  };

  push([co?.name ?? 'Company'], true);
  if (co?.legalName && co.legalName !== co.name) push([co.legalName]);
  const location = companyLocation();
  if (location) push([location]);
  const contact = [co?.phone, co?.email].filter(Boolean).join(' · ');
  if (contact) push([contact]);
  if (ids.length > 0) push([ids.map((i) => `${i.label} ${i.value}`).join('  ·  ')]);
  push([]);

  push([report.title], true);
  if (report.meta) push([report.meta]);
  push([]);

  push(
    report.columns.map((c) => c.label),
    true,
  );
  for (const raw of report.rows) {
    const row = reportRow(raw);
    push(
      report.columns.map((_, i) => row.cells[i] ?? ''),
      row.strong,
    );
  }

  if (report.notes?.length) {
    push([]);
    for (const note of report.notes) push([note]);
  }

  // The heading block above is prose in column A, so the coercion applies to the
  // table's own columns and only to the ones the report right-aligns.
  const numericColumns = new Set(
    report.columns.flatMap((c, i) => (c.align === 'right' ? [i] : [])),
  );

  const stamped = new Date().toISOString().slice(0, 10);
  const name = `${slug(report.fileName ?? report.title)}-${stamped}.xlsx`;
  download(workbook(sheetName(report.title), rows, bold, numericColumns), name);
}
